import { add, format, startOfWeek } from "date-fns";
import { el, enUS } from "date-fns/locale";
import { getAllMenuWeekDaysOverlappingCalendarMonth, getWeekdayName, toDateFromServer, toDayString } from "../../../functions/src/api/dates";
import { cateringMap } from "../../../functions/src/api/dbConfig";
import { getAdminSchoolKey } from "../../api/firebase";
import { languageFromValue, uppercaseForLang, type Lang, type Language } from "../../api/langConfig";

type PrintMonthlyMenuOptions = {
    dateStart: Date;
    dateEnd: Date;
    menuData: Record<string, unknown> | null | undefined;
    sideDaysData: Record<string, unknown> | null | undefined;
    dessertDaysData: Record<string, unknown> | null | undefined;
    schoolData: Record<string, unknown> | null | undefined;
    courseTypesData: Record<string, unknown> | null | undefined;
    isDraft?: boolean;
    /** Order app passes `currentSchoolKey`; admin omits and uses `getAdminSchoolKey()`. */
    schoolKey?: string | null;
};

export class EmptyMonthlyMenuError extends Error {
    constructor() {
        super("EMPTY_MONTHLY_MENU");
        this.name = "EmptyMonthlyMenuError";
    }
}

type MealLike = {
    courseType?: string;
    menuType?: number;
    isSide?: boolean;
    isDessert?: boolean;
    name?: Lang | string;
    description?: Lang | string;
    sideDish?: Lang | string;
};

type CourseTypeLabels = {
    breakfast?: string;
    morningSnack?: string;
};

function getLang(): Language {
    return languageFromValue(localStorage.getItem("lang"));
}

function getLabel(lang: Language, elText: string, enText: string): string {
    return lang === "en" ? enText : elText;
}

function getLangValue(value: unknown, lang: Language): string {
    if (!value) return "";
    if (typeof value === "string") return value;
    if (typeof value === "object") {
        const langValue = (value as Lang)[lang];
        if (langValue) return langValue;
        const record = value as Lang;
        return record.el ?? record.en ?? "";
    }
    return "";
}

function escapeHtml(input: string): string {
    return input
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
}

/** For print/PDF default filename; strips characters invalid on common filesystems. */
function sanitizeForFilenameSegment(value: string): string {
    return value
        .replace(/[\u0000-\u001f<>:"/\\|?*]/g, "")
        .replace(/\s+/g, " ")
        .trim();
}

function getMonthDays(dateStart: Date, _dateEnd: Date): string[] {
    return getAllMenuWeekDaysOverlappingCalendarMonth(dateStart.getFullYear(), dateStart.getMonth());
}

function monthlyMenuHasContent(
    menuData: Record<string, Record<string, unknown>> | null | undefined,
    days: string[],
): boolean {
    if (!menuData) return false;
    return days.some((dayKey) => {
        const dayData = menuData[dayKey];
        if (!dayData || typeof dayData !== "object") return false;
        return Object.entries(dayData).some(
            ([key, value]) => key !== "sideMenu" && key !== "dessertMenu" && !!value && typeof value === "object",
        );
    });
}

function getCourseTypeOrder(courseType: string): number {
    if (courseType === "breakfast") return 0;
    if (courseType === "morningSnack") return 1;
    return 2;
}

/** Labels aligned with order app MonthlyMenu (Menu1Day). */
function getMenuLabel(meal: MealLike, lang: Language, courseTypes: CourseTypeLabels): string {
    if (meal.isSide) {
        return getLabel(lang, "\u03a3\u03a5\u039d\u039f\u0394\u0395\u03a5\u03a4\u0399\u039a\u039f", "SIDE DISH");
    }
    if (meal.isDessert) {
        return getLabel(lang, "\u0395\u03a0\u0399\u0394\u039f\u03a1\u03a0\u0399\u039f", "DESSERT");
    }
    if (meal.courseType === "breakfast") {
        return courseTypes.breakfast ?? getLabel(lang, "\u03a0\u03c1\u03c9\u03b9\u03bd\u03cc", "Breakfast");
    }
    if (meal.courseType === "morningSnack") {
        return courseTypes.morningSnack ?? getLabel(lang, "\u0394\u03b5\u03ba\u03b1\u03c4\u03b9\u03b1\u03bd\u03cc", "Morning snack");
    }
    const menuWord = getLabel(lang, "\u039c\u0395\u039d\u039f\u03a5", "MENU");
    return `${menuWord} ${meal.menuType ?? "-"}`;
}

function buildPrintHtml(
    documentTitle: string,
    headingTitle: string,
    subtitle: string,
    weekdayHeadersHtml: string,
    bodyRowsHtml: string,
    lunchboxLogoUrl: string,
    cateringLogoUrl: string,
    isDraft: boolean,
    draftBannerTitle: string,
    draftBannerSubtitle: string,
): string {
    const draftStyles = isDraft ? `
    .draft-banner {
      margin-bottom: 16px;
      padding: 12px 16px;
      text-align: center;
      border: 3px solid #dc2626;
      border-radius: 8px;
      background: #fef2f2;
      color: #991b1b;
      box-shadow: inset 0 0 0 1px rgba(220, 38, 38, 0.15);
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .draft-banner-title {
      font-size: 18px;
      font-weight: 800;
      letter-spacing: 0.14em;
      text-transform: uppercase;
      line-height: 1.2;
    }
    .draft-banner-subtitle {
      margin-top: 6px;
      font-size: 13px;
      font-weight: 600;
      line-height: 1.35;
      color: #b91c1c;
    }` : "";

    const draftBannerMarkup = isDraft ? `
  <div class="draft-banner" role="status">
    <div class="draft-banner-title">${escapeHtml(draftBannerTitle)}</div>
    <div class="draft-banner-subtitle">${escapeHtml(draftBannerSubtitle)}</div>
  </div>` : "";

    return `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(documentTitle)}</title>
  <style>
    :root {
      --lb-green: #01ABAE;
      --lb-teal-dark: #003738;
      --lb-green-bgr: #D9F2F3;
      --text-title: #1F2933;
      --text-body: #262626;
      --text-secondary: #7B8794;
      --border: #E7E7EE;
      --surface-muted: #F5F7FA;
      --surface-page: #fafbfc;
    }
    * { box-sizing: border-box; }
    body {
      font-family: "Roboto", "Helvetica Neue", Arial, sans-serif;
      margin: 24px;
      color: var(--text-body);
      background: var(--surface-page);
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    ${draftStyles}
    .print-header {
      display: grid;
      grid-template-columns: 1fr 2fr 1fr;
      align-items: center;
      gap: 16px;
      margin-bottom: 16px;
      padding-bottom: 14px;
      border-bottom: 2px solid var(--lb-green);
    }
    .print-logo { height: 44px; width: auto; object-fit: contain; }
    .print-logo.left { justify-self: start; }
    .print-logo.right { justify-self: end; }
    .print-header-text { text-align: center; }
    h1 {
      margin: 0;
      font-size: 22px;
      font-weight: 700;
      color: var(--lb-teal-dark);
      letter-spacing: -0.02em;
    }
    .print-subtitle {
      margin: 6px 0 0;
      font-size: 13px;
      font-weight: 500;
      color: var(--text-secondary);
    }
    table { width: 100%; border-collapse: collapse; table-layout: fixed; margin-top: 64px; }
    th, td {
      border: 1px solid var(--border);
      padding: 6px 8px;
      vertical-align: top;
      font-size: 12px;
      text-align: left;
    }
    th {
      font-weight: 700;
      text-align: center;
      color: #fff;
      background: var(--lb-green);
      border-color: #019a9d;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    tbody td { background: #fff; }
    .cal-cell { min-height: 120px; }
    .cell-date {
      font-weight: 700;
      margin-bottom: 6px;
      padding: 6px 4px 8px;
      text-align: center;
      white-space: nowrap;
      color: var(--lb-teal-dark);
      background: var(--lb-green-bgr);
      border-bottom: 1px solid rgba(1, 171, 174, 0.35);
      border-radius: 2px 2px 0 0;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .meal-block { margin-bottom: 10px; }
    .meal-title { font-weight: 700; color: var(--text-title); }
    .meal-name { margin-top: 2px; color: var(--text-body); }
    .meal-desc { margin-top: 2px; font-size: 11px; color: var(--text-secondary); line-height: 1.35; }
    .cell-closed {
      font-style: italic;
      text-align: center;
      padding-top: 16px;
      color: var(--text-secondary);
    }
    .cell-empty {
      background: var(--surface-muted);
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    @media print {
      body { margin: 10mm; background: #fff; }
      h1 { font-size: 18px; }
      th, td { font-size: 11px; }
      tr, td, .cal-cell { break-inside: avoid; page-break-inside: avoid; }
      .draft-banner {
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
    }
  </style>
</head>
<body>
  ${draftBannerMarkup}
  <div class="print-header">
    <img class="print-logo left" src="${escapeHtml(cateringLogoUrl)}" alt="Catering logo" />
    <div class="print-header-text">
      <h1>${escapeHtml(headingTitle)}</h1>
      <div class="print-subtitle">${escapeHtml(subtitle)}</div>
    </div>
    <img class="print-logo right" src="${escapeHtml(lunchboxLogoUrl)}" alt="Lunchbox logo" />
  </div>
  <table>
    <thead>
      <tr>${weekdayHeadersHtml}</tr>
    </thead>
    <tbody>
      ${bodyRowsHtml}
    </tbody>
  </table>
</body>
</html>`;
}

function printHtmlInHiddenFrame(html: string): void {
    const iframe = document.createElement("iframe");
    iframe.setAttribute(
        "aria-hidden",
        "true",
    );
    iframe.style.cssText =
        "position:fixed;top:0;left:-10000px;width:210mm;height:297mm;border:0;visibility:hidden;pointer-events:none";
    document.body.appendChild(iframe);

    const win = iframe.contentWindow;
    if (!win) {
        iframe.remove();
        return;
    }

    const doc = win.document;
    doc.open();
    doc.write(html);
    doc.close();

    let cleaned = false;
    const cleanup = () => {
        if (cleaned) return;
        cleaned = true;
        iframe.remove();
    };

    win.addEventListener("afterprint", cleanup);

    const runPrint = () => {
        win.focus();
        win.print();
        window.setTimeout(cleanup, 2000);
    };

    const whenImagesReady = () =>
        Promise.all(
            Array.from(doc.images).map(
                (img) =>
                    img.complete
                        ? Promise.resolve()
                        : new Promise<void>((resolve) => {
                              img.addEventListener("load", () => resolve(), { once: true });
                              img.addEventListener("error", () => resolve(), { once: true });
                          }),
            ),
        );

    const schedulePrint = () => {
        void whenImagesReady().then(() => window.setTimeout(runPrint, 0));
    };

    if (doc.readyState === "complete") {
        schedulePrint();
    } else {
        win.onload = schedulePrint;
    }
}

export async function printMonthlyMenu({
    dateStart,
    dateEnd,
    menuData,
    sideDaysData,
    dessertDaysData,
    schoolData,
    courseTypesData,
    isDraft = false,
    schoolKey: schoolKeyOverride,
}: PrintMonthlyMenuOptions): Promise<void> {
    const lang = getLang();
    const days = getMonthDays(dateStart, dateEnd);
    if (!days.length) {
        throw new EmptyMonthlyMenuError();
    }

    const menu = (menuData ?? {}) as Record<string, Record<string, unknown>>;
    if (!monthlyMenuHasContent(menu, days)) {
        throw new EmptyMonthlyMenuError();
    }

    const sideDays = (sideDaysData ?? {}) as Record<string, Record<Language, string>>;
    const dessertDays = (dessertDaysData ?? {}) as Record<string, Record<Language, string>>;
    const school = (schoolData ?? {}) as Record<string, unknown>;
    const courseTypesVal = (courseTypesData ?? {}) as Record<string, Partial<Record<Language, string>>>;
    const courseTypeLabels: CourseTypeLabels = {
        breakfast: getLangValue(courseTypesVal.breakfast, lang),
        morningSnack: getLangValue(courseTypesVal.morningSnack, lang),
    };

    const schoolKey = schoolKeyOverride || getAdminSchoolKey();
    const dayKeySet = new Set(days);
    const weekStartTimes = new Set<number>();
    days.forEach((day) => {
        const d = toDateFromServer(day);
        const monday = startOfWeek(d, { weekStartsOn: 1 });
        weekStartTimes.add(monday.getTime());
    });
    const sortedWeekStarts = Array.from(weekStartTimes).sort((a, b) => a - b);

    const weekdayHeadersHtml = ([1, 2, 3, 4, 5] as const)
        .map((dow) => `<th>${escapeHtml(uppercaseForLang(getWeekdayName(dow, lang, "long"), lang))}</th>`)
        .join("");

    const rows: string[] = [];

    sortedWeekStarts.forEach((weekStartMs) => {
        const cells: string[] = [];
        const mondayDate = new Date(weekStartMs);
        for (let i = 0; i < 5; i++) {
            const cellDate = add(mondayDate, { days: i });
            const dayKey = toDayString(cellDate);
            if (!dayKeySet.has(dayKey)) {
                cells.push(`<td class="cal-cell cell-empty"></td>`);
                continue;
            }

            const weekdayNum = cellDate.getDay();
            const dayData = menu[dayKey] ?? {};

            const sideDay = getLangValue(sideDays[String(weekdayNum)], lang);
            const dessertDay = getLangValue(dessertDays[String(weekdayNum)], lang);
            const sideDish = dayData.sideMenu as MealLike | undefined;
            const dessertMeal = dayData.dessertMenu as MealLike | undefined;
            const sideMenu = sideDish ? getLangValue(sideDish.name, lang) : "";
            const sideName = sideMenu || sideDay;
            const dessertMenu = dessertMeal ? getLangValue(dessertMeal.name, lang) : "";
            const dessertName = dessertMenu || dessertDay;

            const menuEntries = Object.entries(dayData).filter(
                ([key, value]) => key !== "sideMenu" && key !== "dessertMenu" && !!value && typeof value === "object",
            );
            const hasMenuMeal = menuEntries.length > 0;
            const hasAnySideMenu =
                hasMenuMeal &&
                (!!sideName || menuEntries.some(([, v]) => !!getLangValue((v as MealLike).sideDish, lang)));

            const meals = menuEntries
                .map(([, value]) => value as MealLike)
                .sort((a, b) => getCourseTypeOrder(a.courseType ?? "") - getCourseTypeOrder(b.courseType ?? ""));

            const dateLine = format(cellDate, "d/M");
            let inner: string;
            if (!meals.length) {
                inner = `<div class="cell-date">${escapeHtml(dateLine)}</div><div class="cell-closed">${escapeHtml(
                    getLabel(lang, "\u039a\u03bb\u03b5\u03b9\u03c3\u03c4\u03ac", "Closed"),
                )}</div>`;
            } else {
                const blocks: string[] = [];
                meals.forEach((meal) => {
                    const title = getMenuLabel(meal, lang, courseTypeLabels);
                    const name = getLangValue(meal.name, lang);
                    const desc = getLangValue(meal.description, lang);
                    blocks.push(`<div class="meal-block">
            <div class="meal-title">${escapeHtml(title)}</div>
            ${name ? `<div class="meal-name">${escapeHtml(name)}</div>` : ""}
            ${desc ? `<div class="meal-desc">${escapeHtml(desc)}</div>` : ""}
          </div>`);
                });
                if (hasAnySideMenu) {
                    blocks.push(`<div class="meal-block">
            <div class="meal-title">${escapeHtml(
                getLabel(lang, "\u03a3\u03a5\u039d\u039f\u0394\u0395\u03a5\u03a4\u0399\u039a\u039f", "SIDE DISH"),
            )}</div>
            <div class="meal-name">${escapeHtml(sideName)}</div>
          </div>`);
                }
                if (!!dessertName && hasMenuMeal) {
                    blocks.push(`<div class="meal-block">
            <div class="meal-title">${escapeHtml(
                getLabel(lang, "\u0395\u03a0\u0399\u0394\u039f\u03a1\u03a0\u0399\u039f", "DESSERT"),
            )}</div>
            <div class="meal-name">${escapeHtml(dessertName)}</div>
          </div>`);
                }
                inner = `<div class="cell-date">${escapeHtml(dateLine)}</div>${blocks.join("")}`;
            }
            cells.push(`<td class="cal-cell">${inner}</td>`);
        }
        rows.push(`<tr>${cells.join("")}</tr>`);
    });

    const headingTitle = format(dateStart, "MMMM yyyy", { locale: lang === "en" ? enUS : el });
    const schoolName =
        getLangValue(school.schoolName, lang) ||
        getLangValue(school.schoolName, "el") ||
        schoolKey ||
        getLabel(lang, "\u03a7\u03c9\u03c1\u03af\u03c2 \u03c3\u03c7\u03bf\u03bb\u03b5\u03af\u03bf", "No school selected");
    const subtitle = schoolName;

    const origin = window.location.origin;
    const lunchboxLogoUrl = `${origin}/img-assets/logo-light.svg`;
    const currentCatering = cateringMap[schoolKey ?? ""] ?? cateringMap.default;
    const cateringLogoFilename =
        currentCatering === "Gefsinus"
            ? lang === "el"
                ? "logo-gefsinus-dark-el.png"
                : "logo-gefsinus-dark-en.png"
            : "logo-dark.svg";
    const cateringLogoUrl = `${origin}/img-assets/${cateringLogoFilename}`;

    const schoolNameEn =
        sanitizeForFilenameSegment(
            getLangValue(school.schoolName, "en") ||
                getLangValue(school.schoolName, "el") ||
                schoolKey ||
                "No school selected",
        ) || "School";
    const cateringNameEn = sanitizeForFilenameSegment(currentCatering) || "Catering";
    const monthYearEn = format(dateStart, "MMMM yyyy", { locale: enUS });
    const draftFilenameSuffix = isDraft ? " Draft" : "";
    const documentTitle = `${schoolNameEn} - ${cateringNameEn} Menu ${monthYearEn}${draftFilenameSuffix}`;
    const draftBannerTitle = getLabel(lang, "ΠΡΟΧΕΙΡΟ ΜΕΝΟΥ", "DRAFT MENU");
    const draftBannerSubtitle = getLabel(
        lang,
        "Δεν έχει δημοσιευθεί — μόνο για έλεγχο πριν τη δημοσίευση",
        "Not published — for review before publishing",
    );

    const html = buildPrintHtml(
        documentTitle,
        headingTitle,
        subtitle,
        weekdayHeadersHtml,
        rows.join(""),
        lunchboxLogoUrl,
        cateringLogoUrl,
        isDraft,
        draftBannerTitle,
        draftBannerSubtitle,
    );
    printHtmlInHiddenFrame(html);
}
